import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import {
  Anchor,
  Box,
  Button,
  Card,
  Drawer,
  Group,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { BarChart } from "@mantine/charts";
import dayjs from "dayjs";
import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { DataTable } from "../components/DataTable";
import {
  useGetNotificationLog,
  useGetNotificationReads,
} from "../api/notifications";

const SOURCE_LABELS = {
  announcement: "Announcement",
  session_reminder: "Session reminder",
  poll: "Live poll",
};

const SOURCE_OPTIONS = Object.entries(SOURCE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const sourceLabel = (producer) => SOURCE_LABELS[producer] ?? producer;

const messageTitle = (row) =>
  row.isAggregate
    ? `${sourceLabel(row.producer)} · ${row.messageCount} messages`
    : row.title;

const percent = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

function Rate({ count, of }) {
  if (of === null) return "—";

  return (
    <Group gap={4} wrap="nowrap">
      <Text size="sm" fw={600}>
        {percent(count, of)}%
      </Text>
      <Text size="xs" c="dimmed">
        {count}
      </Text>
    </Group>
  );
}

function SummaryCard({ label, value, caption }) {
  return (
    <Card withBorder radius="md" p="md">
      <Text size="xs" fw={700} c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="xl" fw={700}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {caption}
      </Text>
    </Card>
  );
}

function Summary({ totals }) {
  const { delivered = 0, pushed = 0, opened = 0, read = 0 } = totals ?? {};

  return (
    <SimpleGrid cols={{ base: 1, sm: 3 }} mb="sm">
      <SummaryCard
        label="Delivered"
        value={delivered}
        caption="to notification centres"
      />
      <SummaryCard
        label="Push open rate"
        value={`${percent(opened, pushed)}%`}
        caption={`${opened} of ${pushed} pushes tapped`}
      />
      <SummaryCard
        label="Read rate"
        value={`${percent(read, delivered)}%`}
        caption={`${read} of ${delivered} read in the app`}
      />
    </SimpleGrid>
  );
}

function FunnelStep({ label, count, value, caption }) {
  return (
    <Stack gap={4}>
      <Group justify="space-between">
        <Text size="sm" fw={500}>
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          {count} · {caption}
        </Text>
      </Group>
      <Progress value={value} />
    </Stack>
  );
}

function NotificationStats({ eventId, notification }) {
  const { data } = useGetNotificationReads({
    eventId,
    id: notification.isAggregate ? null : notification.id,
  });

  const { delivered, pushed, opened, read } = notification;

  const readsByHour = (data?.readsByHour ?? []).map((row) => ({
    hour: dayjs(row.hour).format("DD/MM HH:mm"),
    reads: row.count,
  }));

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        {sourceLabel(notification.producer)} ·{" "}
        {notification.isAggregate
          ? dayjs(notification.publishedAt).format("DD/MM/YYYY")
          : `sent ${dayjs(notification.publishedAt).format("DD/MM/YYYY HH:mm")}`}
      </Text>

      <Text size="xs" fw={700} c="dimmed" tt="uppercase">
        Funnel
      </Text>
      <FunnelStep
        label="Delivered to notification centre"
        count={delivered}
        value={100}
        caption="100%"
      />
      <FunnelStep
        label="Push sent"
        count={pushed ?? 0}
        value={percent(pushed, delivered)}
        caption={
          pushed === null
            ? "not pushed"
            : `${percent(pushed, delivered)}% of delivered`
        }
      />
      <FunnelStep
        label="Push opened"
        count={opened}
        value={percent(opened, pushed)}
        caption={`${percent(opened, pushed)}% of pushed`}
      />
      <FunnelStep
        label="Read in the app"
        count={read}
        value={percent(read, delivered)}
        caption={`${percent(read, delivered)}% of delivered`}
      />

      {notification.producer === "announcement" && notification.sourceId && (
        <Anchor
          component={Link}
          to={`/app/announcements/${notification.sourceId}`}
          size="sm"
        >
          Open announcement
        </Anchor>
      )}

      {!notification.isAggregate && (
        <>
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="md">
            Reads by hour
          </Text>
          {readsByHour.length > 0 ? (
            <BarChart
              h={200}
              data={readsByHour}
              dataKey="hour"
              maxBarWidth={32}
              series={[{ name: "reads", label: "Reads", color: "blue.6" }]}
            />
          ) : (
            <Text size="sm" c="dimmed">
              No reads yet
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}

function Notifications() {
  const { user } = useContext(AuthContext);
  const { eventId } = getCurrentWorkspace(user);

  const [producer, setProducer] = useState(null);
  const [[dateFrom, dateTo], setDateRange] = useState([null, null]);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useGetNotificationLog({
    eventId,
    producer,
    dateFrom,
    dateTo: dateFrom ? dateTo : null,
    page,
    limit: 20,
  });

  return (
    <Box py="sm">
      <Summary totals={data?.totals} />

      <DataTable
        records={data?.data}
        fetching={isLoading}
        onRowClick={({ record }) => setSelected(record)}
        columns={[
          {
            accessor: "title",
            title: "Message",
            render: (row) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {messageTitle(row)}
                </Text>
                {!row.isAggregate && row.body && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {row.body}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: "producer",
            title: "Source",
            render: (row) => sourceLabel(row.producer),
            filter: (
              <Select
                label="Filter by source"
                placeholder="All sources"
                data={SOURCE_OPTIONS}
                value={producer}
                onChange={(value) => {
                  setProducer(value);
                  setPage(1);
                }}
                comboboxProps={{ withinPortal: false }}
                clearable
              />
            ),
            filtering: !!producer,
          },
          {
            accessor: "publishedAt",
            title: "Sent",
            filter: (
              <Stack gap="xs">
                <DatePicker
                  type="range"
                  value={[dateFrom, dateTo]}
                  onChange={(value) => {
                    setDateRange(value);
                    setPage(1);
                  }}
                  allowSingleDateInRange
                />
                {dateFrom && (
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => {
                      setDateRange([null, null]);
                      setPage(1);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            ),
            filtering: !!dateFrom,
            render: (row) =>
              dayjs(row.publishedAt).format(
                row.isAggregate ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm"
              ),
          },
          { accessor: "delivered", title: "Delivered" },
          {
            accessor: "pushed",
            title: "Pushed",
            render: (row) =>
              row.pushed === null ? (
                <Text size="sm" c="dimmed">
                  Not pushed
                </Text>
              ) : (
                row.pushed
              ),
          },
          {
            accessor: "opened",
            title: "Opened",
            render: (row) => <Rate count={row.opened} of={row.pushed} />,
          },
          {
            accessor: "read",
            title: "Read",
            render: (row) => <Rate count={row.read} of={row.delivered} />,
          },
        ]}
        page={page}
        onPageChange={setPage}
        recordsPerPage={data?.pagination?.perPage ?? 20}
        totalRecords={data?.pagination?.total}
      />

      <Drawer
        opened={!!selected}
        onClose={() => setSelected(null)}
        position="right"
        title={selected && messageTitle(selected)}
      >
        {selected && (
          <NotificationStats eventId={eventId} notification={selected} />
        )}
      </Drawer>
    </Box>
  );
}

export default Notifications;
